
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarHeader, 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton,
  SidebarFooter 
} from '@/components/ui/sidebar';
import { User, LogOut, MessageSquare, Settings, LayoutDashboard, Store } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useUnreadMessagesCount } from '@/hooks/useMessages';

interface DashboardSidebarProps {
  userType: 'breeder' | 'buyer';
  userId: number;
  activeSection: string;
  onSectionChange: (section: string) => void;
  userName: string;
  subscriptionActive?: boolean;
}

export const DashboardSidebar = ({ 
  userType, 
  userId, 
  activeSection, 
  onSectionChange, 
  userName,
  subscriptionActive = true 
}: DashboardSidebarProps) => {
  const { signOut, user } = useAuth();
  const [signOutLoading, setSignOutLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: unreadCount = 0 } = useUnreadMessagesCount();

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
      navigate('/');
    } catch (error) {
      toast({
        title: "Sign Out Failed",
        description: "An error occurred while signing out.",
        variant: "destructive",
      });
    } finally {
      setSignOutLoading(false);
    }
  };

  const menuItems = userType === 'breeder' 
    ? [
        { id: 'profile', label: 'Profile & Listings', icon: LayoutDashboard },
        { id: 'messages', label: 'Messages', icon: MessageSquare, hasNotification: unreadCount > 0 },
        { id: 'settings', label: 'Settings', icon: Settings }
      ]
    : [
        { id: 'profile', label: 'Profile', icon: LayoutDashboard },
        { id: 'messages', label: 'Messages', icon: MessageSquare, hasNotification: unreadCount > 0 }
      ];

  // Use the actual user ID from auth context
  const profileUrl = user ? `/${userType}/${user.id}` : '#';

  return (
    <Sidebar className="w-64">
      <SidebarHeader className="p-6 border-b">
        <Link to="/" className="flex items-center">
          <h1 className="text-2xl font-extrabold">
            <span className="text-black">ur</span>
            <span className="text-green-800">Cattle</span>
          </h1>
        </Link>
      </SidebarHeader>

      <SidebarContent className="p-4">
        <SidebarMenu>
          {menuItems.map((item) => (
            <SidebarMenuItem key={item.id}>
              <SidebarMenuButton
                onClick={() => onSectionChange(item.id)}
                isActive={activeSection === item.id}
                className="w-full justify-start relative"
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.label}
                {item.hasNotification && (
                  <div className="absolute right-2 w-2 h-2 bg-purple-500 rounded-full"></div>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
          
          <SidebarMenuItem>
            <SidebarMenuButton
              className="w-full justify-start text-green-700 hover:text-green-800"
              onClick={() => navigate('/marketplace')}
            >
              <Store className="w-4 h-4 mr-2" />
              Marketplace
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="p-4 border-t">
        <Link to={profileUrl}>
          <Button variant="outline" className="w-full mb-2">
            <User className="w-4 h-4 mr-2" />
            View Public Profile
          </Button>
        </Link>
        <Button 
          variant="outline" 
          className="w-full text-red-600 hover:text-red-700"
          onClick={handleSignOut}
          disabled={signOutLoading}
        >
          {signOutLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
          ) : (
            <LogOut className="w-4 h-4 mr-2" />
          )}
          {signOutLoading ? 'Signing Out...' : 'Sign Out'}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};
